import { useState, useEffect, useCallback } from 'react';
import { initDB } from '@/lib/db';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { AppSettings } from '@/types/common';
import styles from '@/styles/components/SettingsPage.module.css';

const DEFAULT_SETTINGS: AppSettings = {
  emailAddresses: [],
  reminderTime: '08:00',
  reminderThreshold: 5,
  lastSyncedAt: null,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAILS = 5;

export function SettingsPage() {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);

  // Load settings from IndexedDB
  useEffect(() => {
    async function loadSettings() {
      try {
        const db = await initDB();
        const stored = await db.get('settings', 'user-settings');
        if (stored) {
          setSettings(stored);
        }
      } catch {
        // Use defaults
      }
    }
    loadSettings();
  }, []);

  // Save settings to IndexedDB
  const saveSettings = useCallback(async (updated: AppSettings) => {
    try {
      const db = await initDB();
      await db.put('settings', { ...updated, reminderTime: updated.reminderTime } as AppSettings & { reminderTime: string });
      setSaveStatus('Settings saved');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch {
      setSaveStatus('Failed to save');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  }, []);

  const handleAddEmail = useCallback(() => {
    const email = newEmail.trim();
    if (!email) return;

    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    if (settings.emailAddresses.includes(email)) {
      setEmailError('This email is already added.');
      return;
    }

    if (settings.emailAddresses.length >= MAX_EMAILS) {
      setEmailError(`Maximum ${MAX_EMAILS} email addresses allowed.`);
      return;
    }

    const updated = {
      ...settings,
      emailAddresses: [...settings.emailAddresses, email],
    };
    setSettings(updated);
    setNewEmail('');
    setEmailError('');
    saveSettings(updated);
  }, [newEmail, settings, saveSettings]);

  const handleRemoveEmail = useCallback(
    (email: string) => {
      const updated = {
        ...settings,
        emailAddresses: settings.emailAddresses.filter((e) => e !== email),
      };
      setSettings(updated);
      saveSettings(updated);
    },
    [settings, saveSettings],
  );

  const handleReminderTimeChange = useCallback(
    (time: string) => {
      const updated = { ...settings, reminderTime: time };
      setSettings(updated);
      saveSettings(updated);
    },
    [settings, saveSettings],
  );

  const handleThresholdChange = useCallback(
    (value: string) => {
      const updated = { ...settings, reminderThreshold: Number(value) };
      setSettings(updated);
      saveSettings(updated);
    },
    [settings, saveSettings],
  );

  const handleClearAllData = useCallback(async () => {
    try {
      const db = await initDB();
      await db.clear('starter');
      await db.clear('feedingHistory');
      await db.clear('schedules');
      await db.clear('settings');
      await db.clear('wizard');
      setSettings(DEFAULT_SETTINGS);
      setShowClearDialog(false);
      setSaveStatus('All data cleared');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch {
      setSaveStatus('Failed to clear data');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  }, []);

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Settings</h1>

      {/* Email Addresses */}
      <Card>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Email Notifications</h2>
          <p className={styles.sectionDescription}>
            Receive feeding reminders at these addresses.
          </p>

          <div className={styles.emailList}>
            {settings.emailAddresses.map((email) => (
              <div key={email} className={styles.emailItem}>
                <span className={styles.emailText}>{email}</span>
                <button
                  className={styles.removeEmail}
                  onClick={() => handleRemoveEmail(email)}
                  aria-label={`Remove ${email}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {settings.emailAddresses.length < MAX_EMAILS && (
            <div className={styles.addEmailRow}>
              <input
                type="email"
                className={styles.input}
                placeholder="Add email address"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setEmailError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddEmail();
                }}
                aria-label="New email address"
              />
              <button
                className={styles.addButton}
                onClick={handleAddEmail}
                disabled={!newEmail.trim()}
                aria-label="Add email"
              >
                Add
              </button>
            </div>
          )}

          {emailError && <p className={styles.fieldError}>{emailError}</p>}
          <p className={styles.fieldHint}>
            {settings.emailAddresses.length}/{MAX_EMAILS} addresses
          </p>
        </div>
      </Card>

      {/* Reminder Settings */}
      <Card>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Reminders</h2>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="reminderTime">
              Reminder Time
            </label>
            <input
              id="reminderTime"
              type="time"
              className={styles.input}
              value={settings.reminderTime}
              onChange={(e) => handleReminderTimeChange(e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="reminderThreshold">
              Remind after days without feeding
            </label>
            <select
              id="reminderThreshold"
              className={styles.select}
              value={settings.reminderThreshold}
              onChange={(e) => handleThresholdChange(e.target.value)}
            >
              <option value={5}>5 days</option>
              <option value={6}>6 days</option>
              <option value={7}>7 days</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Change PIN */}
      <Card>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Security</h2>
          <Button variant="secondary" onClick={() => setShowPinDialog(true)}>
            🔒 Change PIN
          </Button>
        </div>
      </Card>

      {/* Danger Zone */}
      <div className={styles.dangerZone}>
        <h2 className={styles.dangerTitle}>Danger Zone</h2>
        <p className={styles.dangerDescription}>
          This will permanently delete all your starter data, feeding history,
          schedules, and settings. This action cannot be undone.
        </p>
        <Button variant="danger" onClick={() => setShowClearDialog(true)}>
          🗑 Clear All Data
        </Button>
      </div>

      {/* Save Status */}
      {saveStatus && <div className={styles.saveStatus}>{saveStatus}</div>}

      {/* Clear Data Confirmation */}
      <ConfirmDialog
        open={showClearDialog}
        title="Clear All Data?"
        message="This will permanently delete all your starter data, feeding history, schedules, and settings. This cannot be undone."
        confirmLabel="Delete Everything"
        cancelLabel="Keep My Data"
        variant="danger"
        onConfirm={handleClearAllData}
        onCancel={() => setShowClearDialog(false)}
      />

      {/* Change PIN Confirmation */}
      <ConfirmDialog
        open={showPinDialog}
        title="Change PIN"
        message="To change your PIN, you'll need to log out and set up a new one during sign-in."
        confirmLabel="OK"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => setShowPinDialog(false)}
        onCancel={() => setShowPinDialog(false)}
      />
    </div>
  );
}
